// ── certificates-collection: useCollection client adapter for /certificates ───

import { useMemo } from "react";
import {
  Badge,
  useCollection,
  type CollectionColumnDef,
  type CollectionFilterDef,
  type UseCollectionResult
} from "@vjal/ui";

import { readDirectoryInitialState } from "../directory-collection-state";
import { certificateStatusTone, hasCertificateArtifact, orDash, type CertificateRow } from "./certificates-helpers";

export const CERTIFICATES_PAGE_SIZE = 25;
export const CERTIFICATES_DEFAULT_SORT = { id: "issuedAt", dir: "desc" as const };

export function buildCertificatesFilterDefs(): CollectionFilterDef<CertificateRow>[] {
  return [
    {
      id: "status",
      label: "Status",
      type: "select",
      placeholder: "All statuses",
      getValue: (row) => row.status ?? "",
      options: [
        { value: "issued", label: "Issued" },
        { value: "revoked", label: "Revoked" },
        { value: "pending", label: "Pending" }
      ]
    }
  ];
}

export function buildCertificatesCollectionColumns(): CollectionColumnDef<CertificateRow>[] {
  return [
    {
      id: "id",
      header: "Certificate",
      sortable: true,
      sortAccessor: (row) => row.id,
      searchValue: (row) => `${row.id} ${row.participantId ?? ""} ${row.cohortId ?? ""}`,
      priority: 1,
      cell: (row) => <span className="font-medium text-strong">#{row.id}</span>
    },
    {
      id: "participantId",
      header: "Participant",
      sortable: true,
      sortAccessor: (row) => row.participantId ?? "",
      priority: 2,
      cell: (row) => <span>{orDash(row.participantId)}</span>
    },
    {
      id: "cohortId",
      header: "Cohort",
      sortable: true,
      sortAccessor: (row) => row.cohortId ?? "",
      priority: 2,
      cell: (row) => <span>{orDash(row.cohortId)}</span>
    },
    {
      id: "status",
      header: "Status",
      sortable: true,
      sortAccessor: (row) => row.status ?? "",
      priority: 2,
      cell: (row) => (
        <Badge variant={certificateStatusTone(row.status) === "danger" ? "danger" : certificateStatusTone(row.status)}>
          {row.status}
        </Badge>
      )
    },
    {
      id: "issuedAt",
      header: "Issued",
      sortable: true,
      sortAccessor: (row) => row.issuedAt ?? "",
      priority: 2,
      cell: (row) => <span className="text-sm text-ink-muted">{orDash(row.issuedAt)}</span>
    },
    {
      id: "artifactRef",
      header: "Document",
      sortable: false,
      priority: 2,
      cell: (row) =>
        hasCertificateArtifact(row) ? (
          <Badge variant="success">Document ready</Badge>
        ) : (
          <Badge variant="warning">Document pending</Badge>
        )
    }
  ];
}

export function useCertificatesCollection(
  rows: CertificateRow[],
  routePath?: string
): UseCollectionResult<CertificateRow> {
  const filters = useMemo(() => buildCertificatesFilterDefs(), []);
  const columns = useMemo(() => buildCertificatesCollectionColumns(), []);
  const initialState = useMemo(
    () => ({ sort: CERTIFICATES_DEFAULT_SORT, ...readDirectoryInitialState(routePath, CERTIFICATES_PAGE_SIZE) }),
    [routePath]
  );

  return useCollection<CertificateRow>({
    mode: "client",
    rows,
    columns,
    filters,
    pageSize: CERTIFICATES_PAGE_SIZE,
    urlSync: "query",
    views: "coaching-certificates",
    initialState,
    getRowKey: (row) => row.id
  });
}
